import { useEffect, useRef } from 'react';

const steps = [
  {
    icon: 'ri-phone-line',
    step: '01',
    title: 'Book a Demo Call',
    desc: "Schedule a free 30-minute call with our team. We'll learn about your business, understand your pain points, and show you exactly how AI automation applies to your situation.",
  },
  {
    icon: 'ri-tools-line',
    step: '02',
    title: 'Custom Setup & Build',
    desc: "We design and build your bespoke automation system: chatbot, booking flow, CRM setup, and lead sequences. Build time is 30 to 60 days depending on complexity. We handle everything, zero input needed from you.",
  },
  {
    icon: 'ri-rocket-2-line',
    step: '03',
    title: 'Go Live & Scale',
    desc: "We launch your AI system and monitor everything. You start seeing more bookings, fewer missed leads, and hours saved every week. Then we help you scale.",
  },
];

const HowItWorksSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.step-card').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateX(0)';
              }, i * 200);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-24 relative overflow-hidden" ref={sectionRef}>
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-ea-navy via-ea-black to-ea-navy"></div>
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'radial-gradient(circle, #0ea5e9 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
            <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">How It Works</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-5">
            Get Started in{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
              3 Simple Steps
            </span>
          </h2>
          <p className="text-ea-gray text-lg max-w-2xl mx-auto">
            We make it incredibly easy to get AI working in your business. No tech knowledge needed.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8 relative">
          {/* Connector line (desktop only) */}
          <div className="hidden lg:block absolute top-16 left-1/6 right-1/6 h-px bg-gradient-to-r from-transparent via-ea-blue/30 to-transparent pointer-events-none"></div>

          {steps.map((step, index) => (
            <div
              key={step.step}
              className="step-card relative text-center group"
              style={{
                opacity: 0,
                transform: 'translateX(-20px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
              }}
            >
              {/* Step number circle */}
              <div className="flex justify-center mb-4 sm:mb-6">
                <div className="relative">
                  <div
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center border-2 border-ea-blue bg-ea-dark-card"
                    style={{ boxShadow: '0 0 20px rgba(14, 165, 233, 0.3)' }}
                  >
                    <i className={`${step.icon} text-ea-blue text-lg sm:text-2xl`}></i>
                  </div>
                  <span className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-ea-blue text-white text-[10px] sm:text-xs font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
              </div>

              <div className="bg-ea-dark-card border border-ea-dark-border group-hover:border-ea-blue/40 rounded-xl p-4 sm:p-7 transition-all duration-300">
                <p className="text-ea-blue text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-1 sm:mb-2">Step {step.step}</p>
                <h3 className="text-white font-bold text-base sm:text-xl mb-2 sm:mb-3">{step.title}</h3>
                <p className="text-ea-gray text-xs sm:text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
